/**
 * tapGuard — a Svelte action that fires `onTap` ONLY when the pointer barely
 * moved between pointerdown and pointerup (accidental-tap fix). On a phone a
 * scroll-drag that starts and ends on a row otherwise registers as a tap and
 * opens the wrong thing. The decision is the pure `isTap` (shared TAP_SLOP), so
 * the row guard and the swipe deck agree on one threshold.
 *
 * Usage:  <div use:tapGuard={() => open(i)} ...>   (replace the bare onclick)
 *
 * Does NOT preventDefault and does NOT capture the pointer: native scrolling must
 * still happen. A swallowed activation simply means we never call the handler.
 * Keyboard activation (Enter/Space) is a SEPARATE path (the node's own
 * onkeydown) — once a focusable descendant control exists (e.g. a corrected-
 * length chip button), that onkeydown needs the same target-aware bail this
 * action does, or Enter/Space on the descendant fires the node's handler
 * instead of the descendant's own. See SetRibbon.svelte's onKey /
 * keydownBelongsToNestedControl for that keyboard-side mirror of this check.
 */
import { isTap } from "../swipeDeckLogic";

export function tapGuard(node: HTMLElement, onTap: () => void) {
  let handler = onTap;
  let startX = 0;
  let startY = 0;
  let tracking = false;

  function down(e: PointerEvent) {
    // A tap that lands on a real interactive control INSIDE the node (the
    // stroke-picker tiles and "Never mind" are <button>s) belongs to that
    // control, not to the card. tapGuard attaches RAW pointer listeners on the
    // card; a Svelte 5 `onpointerdown` stopPropagation on a descendant is
    // DELEGATED (it runs at the app root, too late) so it cannot shield this
    // raw listener. Without this bail, a tile tap bubbles to the card, tapGuard
    // reads it as a zero-movement card tap, and togglePicker() closes the picker
    // before the tile's click can commit — the "cannot change a stroke" bug
    // (2026-06-17). The node itself is a role="button" DIV (not a <button>
    // element), so taps on the card surface still arm normally.
    const target = e.target as Element | null;
    if (target && target.closest("button, a, input, select, textarea")) return;
    tracking = true;
    startX = e.clientX;
    startY = e.clientY;
  }
  function up(e: PointerEvent) {
    if (!tracking) return;
    tracking = false;
    if (isTap(startX, startY, e.clientX, e.clientY)) handler();
  }
  function cancel() {
    tracking = false;
  }

  node.addEventListener("pointerdown", down);
  node.addEventListener("pointerup", up);
  node.addEventListener("pointercancel", cancel);

  return {
    update(next: () => void) {
      handler = next;
    },
    destroy() {
      node.removeEventListener("pointerdown", down);
      node.removeEventListener("pointerup", up);
      node.removeEventListener("pointercancel", cancel);
    },
  };
}
